// import { useSubjectMember } from '@healthblocks-io/core'
import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { mixpanel } from '@/mixpanel'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useFeatureFlag } from '@/utils/feature-flag'

import type { SourceCardProps } from '@/modules/shared/components/source-card/SourceCard'

export type CareQuestionOption = {
  id: string
  icon: SourceCardProps['icon']
  color?: string
  label: string
  onClick: () => void
}

/**
 *  Returns the options for the care question
 * @param options  - isDesktop: boolean (hide some options on desktop)
 */
export const useCareQuestionOptions = (options?: { isDesktop: boolean }): CareQuestionOption[] => {
  const subjectMember = useSubjectMember()
  const theme = useTheme()
  const navigate = useNavigate()

  const { showSuccessToast } = useToast()

  const isRemecarePatient = useMemo(
    () => subjectMember?.identifiers?.some((identifier) => identifier.system === 'REMECARE'),
    [subjectMember],
  )

  const defaultOptions: CareQuestionOption[] = options?.isDesktop
    ? []
    : [
        {
          id: 'care-question.options.appointment',
          icon: ['fal', 'calendar'],
          label: t({
            id: 'care-question.options.appointment',
            message: 'Een afspraak maken',
          }),
          onClick: () => {
            mixpanel.track('care_question_option_selected', {
              option: 'appointment',
            })
            navigate('/appointment')
          },
        },
      ]

  if (useFeatureFlag('care_question'))
    defaultOptions.push({
      id: 'care-question.options.medical-question',
      icon: ['fal', 'question-circle'],
      label: t({
        id: 'care-question.options.question',
        message: 'Een vraag stellen',
      }),
      onClick: () => {
        mixpanel.track('care_question_option_selected', {
          option: 'question',
        })
        navigate('/care-question/question')
      },
    })

  if (isRemecarePatient) {
    defaultOptions.push({
      id: 'care-question.options.symptom',
      icon: { path: '/img/svg/remecare-default.svg', alt: 'Remecare logo' },
      label: t({
        id: 'care-question.options.symptom',
        message: 'Een symptoom melden',
      }),
      onClick: () => {
        mixpanel.track('care_question_option_selected', {
          option: 'symptom',
        })
        showSuccessToast({
          message: t({
            id: 'care-question.options.symptom-error',
            message: 'Dit is nog niet beschikbaar. Bedankt voor je interesse.',
          }),
        })
      },
    })
  }

  defaultOptions.push({
    id: 'care-question.options.emergency',
    icon: ['fal', 'exclamation-triangle'],
    label: t({
      id: 'care-question.options.emergency',
      message: 'Ik heb dringend hulp nodig',
    }),
    color: theme.Colors.Status.Error,
    onClick: () => {
      mixpanel.track('care_question_option_selected', {
        option: 'emergency',
      })
      navigate('/care-question/emergency')
    },
  })

  return defaultOptions
}
